/**
 * Utility functions for file operations
 */

export interface IUserFileInteractions {
  /**
   * Opens a file dialog and allows the user to select a file with the specified extension
   * @returns Promise that resolves to the selected File or null if cancelled
   */
  selectFile(extension: string): Promise<File | null>;

  /**
   * Downloads a file from base64 data
   * @param base64Data The base64 encoded file data
   * @param fileName The name for the downloaded file
   * @param mimeType The MIME type of the file.
   */
  downloadFile(base64Data: string, fileName: string, mimeType: string): void;
}

// NOTE: These user interactions are hard to meaninfully unit test, and mostly end up testing the
// implementation rather than the functionality.
/* istanbul ignore next -- @preserve */
export const UserFileInteractions: IUserFileInteractions = {
  selectFile(extension: string): Promise<File | null> {
    return new Promise<File | null>((resolve) => {
      const input = document.createElement("input");
      input.type = "file";
      input.accept = extension;
      input.style.display = "none";

      const cleanup = () => {
        if (document.body.contains(input)) {
          document.body.removeChild(input);
        }
      };
      const handleChange = () => {
        const file = input.files?.[0] || null;
        cleanup();
        resolve(file);
      };
      const handleCancel = () => {
        cleanup();
        resolve(null);
      };

      input.addEventListener("change", handleChange);
      input.addEventListener("cancel", handleCancel);

      try {
        document.body.appendChild(input);
        input.click();
      } catch (domError) {
        cleanup();
        console.warn("Failed to create file input element:", domError);
        resolve(null);
      }
    });
  },

  downloadFile(base64Data, fileName, mimeType): void {
    const blob = base64ToBlob(base64Data, mimeType);
    const url = URL.createObjectURL(blob);

    try {
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      link.style.display = "none";

      // Temporarily add to DOM, click, then remove
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } finally {
      // Always clean up the object URL to free memory
      URL.revokeObjectURL(url);
    }
  },
};

/**
 * Convert Blob to base64 encoded string of blob contents.
 * @param blob The blob to convert to base64
 * @returns Promise base64 encoded content of blob
 * @see https://developer.mozilla.org/en-US/docs/Web/API/FileReader/readAsDataURL
 */
export async function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // reader.result is always a string here (Data URL)
      const base64Data = (reader.result as string).split(",")[1];
      resolve(base64Data);
    };
    reader.onerror = () =>
      reject(reader.error instanceof Error ? reader.error : new Error(String(reader.error)));
    reader.readAsDataURL(blob);
  });
}

/**
 * Converts a base64 string to a Blob
 * @param base64Data The base64 encoded data
 * @param mimeType The MIME type of the file
 * @returns Blob
 */
export function base64ToBlob(base64Data: string, mimeType: string): Blob {
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return new Blob([bytes], { type: mimeType });
}
